import { useAppSelector } from '../../../hooks/useAppSelector';
import type { ACLGroup, RBACGroup } from '../../../types';
import { useQueryErrorToast } from '../../graph/hooks/useQueryErrorToast';
import {
  useGetRoleGroupACLGroupMembershipQuery,
  useGetUserTransitiveACLMembershipQuery,
} from '../../graph/services/graphApi';
import StagedCountBadges from '../../roles/components/StagedCountBadges';
import {
  selectPendingRoleAdds,
  selectPendingRoleDeletes,
  selectSelectedRoleGroupId,
} from '../services/roleMembershipSlice';

import GroupDisplayItem from './GroupDisplayItem';

type Props = {
  groups?: RBACGroup[];
  userId?: string;
  // groupId -> direct group(s) that confer it, for directly-held ACL provenance
  inheritance?: Record<string, RBACGroup[]>;
};

export default function ACLGroupList({ groups, userId, inheritance }: Props) {
  const pendingAdds = useAppSelector(selectPendingRoleAdds);
  const pendingDeletes = useAppSelector(selectPendingRoleDeletes);
  const selectedGroupId = useAppSelector(selectSelectedRoleGroupId);

  const isACLGroup = (group: RBACGroup): group is ACLGroup => {
    return 'parentGroupID' in group;
  };

  const groupIds = groups?.map((group) => group.id!);

  // role id -> role name, so each ACL can show which role it was derived from
  const roleNameById = new Map<string, string>();
  for (const role of [...(groups ?? []), ...pendingAdds]) {
    if (role.id) roleNameById.set(role.id, role.displayName!);
  }

  // Graph API queries
  const { data: aclGroups, isError: aclGroupsError } = useGetRoleGroupACLGroupMembershipQuery(
    groupIds || [],
    {
      skip: !groupIds,
    }
  );
  useQueryErrorToast(aclGroupsError, 'Failed to load calculated ACL memberships');
  const { data: pendingAddsAclGroups } = useGetRoleGroupACLGroupMembershipQuery(
    pendingAdds.map((group) => group.id!)
  );
  // ACLs the user holds directly (not via a role), minus any already shown as
  // role-derived above, so the same ACL isn't listed twice.
  const { data: directAclMembership, isError: directAclError } =
    useGetUserTransitiveACLMembershipQuery(userId ?? '', {
      skip: !userId,
    });
  useQueryErrorToast(directAclError, 'Failed to load directly-held ACL memberships');
  // Merge role-derived ACLs by id. An ACL granted by several roles otherwise shows
  // once per role; instead show a single row that lists every granting role in its
  // "from …" caption. We also resolve the EFFECTIVE add/remove state from the full
  // set of granting roles: an ACL is only "added" (green) if no role already grants
  // it, and only "removed" (red) if every granting role is being removed.
  type MergedAcl = {
    id: string;
    displayName: string;
    sourceRoleIds: string[];
    fromCurrent: boolean; // granted by a role the user currently holds
    fromPendingAdd: boolean; // granted by a role being added
  };
  const mergedById = new Map<string, MergedAcl>();
  const collect = (acls: RBACGroup[] | undefined, kind: 'current' | 'pendingAdd') => {
    for (const acl of acls ?? []) {
      if (!isACLGroup(acl)) continue;
      const merged = mergedById.get(acl.id) ?? {
        displayName: acl.displayName,
        fromCurrent: false,
        fromPendingAdd: false,
        id: acl.id,
        sourceRoleIds: [],
      };
      if (!merged.sourceRoleIds.includes(acl.parentGroupID)) {
        merged.sourceRoleIds.push(acl.parentGroupID);
      }
      if (kind === 'current') merged.fromCurrent = true;
      else merged.fromPendingAdd = true;
      mergedById.set(acl.id, merged);
    }
  };
  collect(aclGroups, 'current');
  collect(pendingAddsAclGroups, 'pendingAdd');

  const pendingDeleteIds = new Set(pendingDeletes.map((group) => group.id));
  const mergedAcls = [...mergedById.values()]
    .map((acl) => {
      // After submit the ACL is still granted if any source role survives — a
      // current role that isn't being deleted, or a pending-add role (never deleted).
      const willHave = acl.sourceRoleIds.some((roleId) => !pendingDeleteIds.has(roleId));
      return {
        added: !acl.fromCurrent && acl.fromPendingAdd,
        deleted: acl.fromCurrent && !willHave,
        derivedFrom: acl.sourceRoleIds
          .map((roleId) => roleNameById.get(roleId))
          .filter(Boolean)
          .sort()
          .join(', '),
        displayName: acl.displayName,
        id: acl.id,
        selected: acl.sourceRoleIds.includes(selectedGroupId),
      };
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  // Gate on userId, not just the skipped query: RTK Query keeps the last fetched
  // `data` across the arg/skip change, so without this the directly-held ACLs
  // would linger after the transitive toggle is switched back off. mergedById's
  // keys are exactly the role-derived ACL ids, so reuse it to drop duplicates.
  const directAcls = userId
    ? (directAclMembership ?? []).filter((acl) => !mergedById.has(acl.id))
    : [];

  // Staged-change summary for the panel header. A queued role add can newly grant
  // ACLs (green); a queued role removal can drop ACLs no surviving role still grants
  // (red). The "current" count includes ACLs staged for removal — they're held until
  // Submit — but excludes the green not-yet-granted previews, mirroring the other panels.
  const addCount = mergedAcls.filter((acl) => acl.added).length;
  const deleteCount = mergedAcls.filter((acl) => acl.deleted).length;
  const currentCount = mergedAcls.length - addCount + directAcls.length;

  if (!groups) {
    return <div></div>;
  }

  return (
    <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-4 flex flex-col flex-1 min-h-0">
      <div className="flex flex-row items-center gap-2 pb-2">
        <span className="font-bold">Calculated ACL membership</span>
        <span className="badge badge-ghost" title="Current calculated ACL memberships">
          {currentCount}
        </span>
        <StagedCountBadges addCount={addCount} deleteCount={deleteCount} />
      </div>
      {mergedAcls.length + directAcls.length === 0 ? (
        <div className="text-sm opacity-60">No calculated ACL memberships.</div>
      ) : (
        <div className="border border-base-200 rounded-lg overflow-hidden flex-1 min-h-0">
          <ul className="divide-y divide-base-200 w-full h-full overflow-y-auto">
            {
              // One row per ACL, listing every granting role in its "from …" caption.
              // Informational (selectable={false}): the source roles are shown inline,
              // so there's nothing to reverse-select — you pick roles in the left list,
              // and selecting one still highlights the ACLs it grants.
              mergedAcls.map((acl) => (
                <GroupDisplayItem
                  key={acl.id}
                  displayName={acl.displayName}
                  roleId={acl.id}
                  selectable={false}
                  selected={acl.selected}
                  derivedFrom={acl.derivedFrom}
                  added={acl.added}
                  deleted={acl.deleted}
                />
              ))
            }
            {
              // ACLs the user is a member of directly (not via a role). These are
              // informational (selectable={false}): there's no parent role to
              // highlight, so they don't participate in role selection. Only the
              // genuinely-nested ones (transitive flag from the query) are badged.
              directAcls.map((acl) => (
                <GroupDisplayItem
                  key={acl.id!}
                  displayName={acl.displayName!}
                  roleId={acl.id!}
                  selectable={false}
                  transitive={'transitive' in acl ? acl.transitive : false}
                  derivedFrom={inheritance?.[acl.id!]?.map((g) => g.displayName).join(', ')}
                />
              ))
            }
          </ul>
        </div>
      )}
    </div>
  );
}
